import { memo, useMemo, useState } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { MediaPreview } from '@features/components/ClinicalData/components/MediaPreview/MediaPreview';
import { useChannelMedia } from './useChannelMedia';
import { MEDIA_TILES_PER_ROW } from './constants';
import { buildVirtualRows, mediaItemsToClinicalMedia, getGroupItemsByMediaId } from './utils';
import { TabLoadingState } from '../components/TabLoadingState';
import { TabEmptyState } from '../components/TabEmptyState';
import { renderVirtualRow } from './components/renderVirtualRow';

type ChatMediaTabProps = {
  inlineLayout?: boolean;
};

export const ChatMediaTab = memo(({ inlineLayout = false }: ChatMediaTabProps) => {
  const { groups, isLoading } = useChannelMedia();
  const tilesPerRow = inlineLayout ? MEDIA_TILES_PER_ROW : 1;

  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedId, setSelectedId] = useState('');

  const currentGroupItems = useMemo(() => getGroupItemsByMediaId(groups, selectedId), [groups, selectedId]);
  const clinicalMedia = useMemo(() => mediaItemsToClinicalMedia(currentGroupItems), [currentGroupItems]);
  const virtualRows = useMemo(() => buildVirtualRows(groups, tilesPerRow), [groups, tilesPerRow]);

  const handleTileClick = (id: string) => {
    setSelectedId(id);
    setPreviewOpen(true);
  };

  const handlePreviewClose = () => {
    setSelectedId('');
    setPreviewOpen(false);
  };

  return (
    <>
      <div className="h-full">
        {isLoading && <TabLoadingState />}
        {!isLoading && groups.length === 0 && <TabEmptyState message="No media yet" />}
        {!isLoading && groups.length > 0 && (
          <Virtuoso
            data={virtualRows}
            context={{ tilesPerRow, onTileClick: handleTileClick }}
            itemContent={renderVirtualRow}
          />
        )}
      </div>
      <MediaPreview
        media={clinicalMedia}
        selectedMediaId={selectedId}
        open={previewOpen}
        onClose={handlePreviewClose}
        useDirectPath
      />
    </>
  );
});
